import { CdkAccordion } from '@angular/cdk/accordion';
import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked, ViewEncapsulation } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/components/button';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import type { FieldError, FieldMode } from '../../core/contracts/field.binding';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { readModelValue } from '../../core/runtime/model.path';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { buildSectionRenderMap, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { countSurfaceTabErrors, resolveCustomTabMount } from '../../core/surface/surface.tab.presenter';
import { WbTabShellComponent, WbTabDeclaration } from '../../core/tabs/wb.tab.shell.component';
import { collectFormHostErrors, isFormHostBusy, isFormHostSubmitDisabled } from '../form/form.host.state';
import { schedulingEditorHostBridgeStyles, schedulingEditorHostStyleId } from './wb.surface.scheduling.editor.host.theme';
import { SCHEDULING_EDITOR_CLOSE_REQUEST } from './scheduling.editor.surface.definition';

/** A feature-neutral submit host. Every tab shares this page; child autosaving surfaces are not allowed. */
@Component({
  selector: 'wb-surface-scheduling-editor-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CdkAccordion, NgComponentOutlet, NgTemplateOutlet, ButtonComponent, ActionBarComponent, WbSectionComponent, WbTabShellComponent, PAGE_COMPONENTS],
  templateUrl: './wb.surface.scheduling.editor.host.component.html',
  styleUrl: './wb.surface.scheduling.editor.host.component.less',
  host: { '[attr.data-surface]': '"schedulingEditor"', '[attr.aria-busy]': '$busy()', '(keydown)': 'onKeydown($event)' },
})
export class WbSurfaceSchedulingEditorHostComponent {
  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });
  private readonly document = inject(DOCUMENT);
  private readonly closeRequests = inject(SCHEDULING_EDITOR_CLOSE_REQUEST, { optional: true });
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;
  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  protected readonly $selectedTab = signal<string | undefined>(undefined);
  protected readonly $submitting = signal(false);
  protected readonly $confirmDiscard = signal(false);
  private readonly $submitErrors = signal<readonly FieldError[]>([]);
  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $busy = computed(() => isFormHostBusy({ initializing: this.$page().$initializing(), submitting: this.$submitting(), saving: this.$page().$saving() }));
  protected readonly $readonly = computed(() => this.$page().$readonly());
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$readonly() || this.$busy() ? 'read' : 'edit');
  protected readonly $submitDisabled = computed(() => isFormHostSubmitDisabled({ readonly: this.$readonly(), busy: this.$busy(), invalid: this.$page().$invalid(), validating: this.$page().$validating() }));
  protected readonly $submitLabel = computed(() => this.$handle().compiled.submitLabel ?? 'Save');
  protected readonly $title = computed(() => {
    const header = this.$handle().compiled.header;
    const value = header ? readModelValue(this.$page().$model(), header.identifier) : undefined;
    return value == null || value === '' ? header?.kind ?? 'Task editor' : String(value);
  });
  protected readonly $errors = computed(() => collectFormHostErrors(this.$page().$surfaceErrors(), this.$submitErrors()));
  private readonly $resolvedActions = computed(() => resolveSurfaceActions(this.$handle()));
  protected readonly $actions = computed(() => toSurfaceActionCommands(this.$resolvedActions(), this.$busy()));
  private readonly $sectionMap = computed(() => buildSectionRenderMap(this.$handle()));
  protected readonly $bodySections = computed(() => visibleSectionsForPaths(this.$handle().compiled.bodySections, this.$sectionMap(), this.$page()).filter(item => item.customRenderer || item.fieldGroups.length || item.repeats.length || item.fields.some(field => !(field.instance?.$hidden() ?? field.field.$hidden()))));
  protected readonly $tabs = computed(() => this.$handle().compiled.tabs.map(tab => {
    if (tab.type !== 'form' && tab.type !== 'custom') {
      throw new Error('Scheduling editor tabs must share the parent draft: use form or custom tabs, not independent child surfaces or relation boards.');
    }
    return { tab, sections: visibleSectionsForPaths(tab.sections, this.$sectionMap(), this.$page()), custom: resolveCustomTabMount(tab, this.$handle()) };
  }));
  protected readonly $tabDeclarations = computed<readonly WbTabDeclaration[]>(() => this.$tabs().map(({ tab, sections }) => ({
    value: tab.path, label: this.$page().tab(tab.path).$title() ?? tab.title,
    visible: !this.$page().tab(tab.path).$hidden(), errorCount: countSurfaceTabErrors(this.$page(), tab, sections),
  })));
  protected readonly $activeTab = computed(() => this.$tabs().find(item => item.tab.path === this.$selectedTab() && !this.$page().tab(item.tab.path).$hidden()) ?? this.$tabs().find(item => !this.$page().tab(item.tab.path).$hidden()));

  public constructor() {
    DesignSystem.Mount(this.document, schedulingEditorHostStyleId, schedulingEditorHostBridgeStyles);
    let seen = 0;
    effect(() => {
      const request = this.closeRequests?.() ?? 0;
      if (request > seen) {
        seen = request;
        untracked(() => this.cancel());
      }
    });
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      void this.submit();
    }
  }

  protected cancel(): void {
    if (this.$page().$dirty() && !this.$readonly()) {
      this.$confirmDiscard.set(true);
    } else {
      this.discard();
    }
  }

  protected discard(): void {
    // The supplied kit aborts its pending proposal before closing. Cancel never accepts a draft.
    this.$confirmDiscard.set(false);
    this.$page().cancel();
  }

  protected async submit(): Promise<void> {
    if (this.$submitDisabled()) return;
    this.$confirmDiscard.set(false);
    this.$submitting.set(true);
    this.$submitErrors.set([]);
    try {
      const result = await this.$page().submit();
      if (!result.ok) this.$submitErrors.set(result.errors ?? []);
    } finally {
      this.$submitting.set(false);
    }
  }
}
